import { Note, Tab, Collection, Task } from '@shared/types';
import { storage } from '@shared/lib/storage';

export interface OfflineOperation {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE' | 'MOVE_TAB' | 'CREATE_COLLECTION' | 'DELETE_TAB' | 'REORDER_COLLECTIONS' | 'REORDER_TABS' | 'CREATE_TASK' | 'UPDATE_TASK' | 'DELETE_TASK' | 'ADD_TASK_LINK' | 'REMOVE_TASK_LINK';
  data: Partial<Note> | Partial<Tab> | Partial<Collection> | Partial<Task> | {
    tabId: string;
    fromCollectionId: string;
    toCollectionId: string;
    newSequence: number;
    tabData: {
      title: string;
      url: string;
      favicon_url?: string;
    };
  } | {
    collections: Array<{ collection_id: string; sequence: number }>;
  } | {
    collectionId: string;
    tabs: Array<{ tab_id: string; sequence: number }>;
  } | {
    task_id: string;
    payload: Record<string, any>;
  } | {
    task_id: string;
    linked_task_id?: string;
    link_type?: string;
    task_link_id?: string;
  };
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

export interface OfflineQueue {
  operations: OfflineOperation[];
  isProcessing: boolean;
}

class OfflineQueueManager {
  private queue: OfflineOperation[] = [];
  private isProcessing = false;
  private listeners: Array<(queue: OfflineQueue) => void> = [];

  // Add operation to queue
  addOperation(operation: Omit<OfflineOperation, 'id' | 'timestamp' | 'retryCount'>): string {
    const id = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newOperation: OfflineOperation = {
      ...operation,
      id,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(newOperation);
    this.saveToLocalStorage();
    this.notifyListeners();
    
    return id;
  }

  // Get all pending operations
  getOperations(): OfflineOperation[] {
    return [...this.queue];
  }

  // Remove operation from queue
  removeOperation(id: string): void {
    this.queue = this.queue.filter(op => op.id !== id);
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Update operation retry count
  updateOperationRetry(id: string, retryCount: number): void {
    const operation = this.queue.find(op => op.id === id);
    if (operation) {
      operation.retryCount = retryCount;
      this.saveToLocalStorage();
      this.notifyListeners();
    }
  }

  // Clear all operations
  clearQueue(): void {
    this.queue = [];
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Replace every occurrence of a locally-created id with the server-assigned
  // id across all queued operations (deep, structural). Used after a CREATE
  // syncs so follow-up operations target the real record.
  remapId(oldId: string, newId: string): void {
    const replace = (value: any): any => {
      if (value === oldId) return newId;
      if (Array.isArray(value)) return value.map(replace);
      if (value && typeof value === 'object') {
        const out: any = {};
        for (const key of Object.keys(value)) out[key] = replace(value[key]);
        return out;
      }
      return value;
    };
    this.queue = this.queue.map(op => ({ ...op, data: replace(op.data) }));
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Get queue status
  getQueueStatus(): OfflineQueue {
    return {
      operations: [...this.queue],
      isProcessing: this.isProcessing,
    };
  }

  // Set processing status
  setProcessing(processing: boolean): void {
    this.isProcessing = processing;
    this.notifyListeners();
  }

  // Subscribe to queue changes
  subscribe(listener: (queue: OfflineQueue) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Save to localStorage
  private saveToLocalStorage(): void {
    try {
      storage.setItem('hintword_offline_queue', JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save offline queue to localStorage:', error);
    }
  }

  // Load from localStorage
  loadFromLocalStorage(): void {
    try {
      const stored = storage.getItem('hintword_offline_queue');
      if (stored) {
        this.queue = JSON.parse(stored);
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Failed to load offline queue from localStorage:', error);
    }
  }

  // Notify listeners
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener(this.getQueueStatus());
    });
  }

  // Get operations by type
  getOperationsByType(type: OfflineOperation['type']): OfflineOperation[] {
    return this.queue.filter(op => op.type === type);
  }

  // Get operations that need retry
  getRetryableOperations(): OfflineOperation[] {
    return this.queue.filter(op => op.retryCount < op.maxRetries);
  }

  // Get operations older than specified time
  getStaleOperations(maxAge: number): OfflineOperation[] {
    const now = Date.now();
    return this.queue.filter(op => (now - op.timestamp) > maxAge);
  }
}

// Export singleton instance
export const offlineQueueManager = new OfflineQueueManager();

// Initialize from localStorage on module load
offlineQueueManager.loadFromLocalStorage();
